/**
 * A simple horizontal health bar rendered with Phaser Graphics.
 * White border, white fill that shrinks as health decreases.
 * Supports a subtle pulsing glow effect to indicate health recovery.
 */

import {
  HUD_BAR_WIDTH,
  HUD_BAR_HEIGHT,
  COLOR_WHITE,
  COLOR_DARK_GRAY,
} from '../config/game-config.js';

export default class HealthBar {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x - Top-left x.
   * @param {number} y - Top-left y.
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.width = HUD_BAR_WIDTH;
    this.height = HUD_BAR_HEIGHT;

    /** @type {Phaser.GameObjects.Graphics} */
    this.graphics = scene.add.graphics();

    /** @type {Phaser.GameObjects.Graphics|null} Glow overlay graphics for rest recovery. */
    this._glowGraphics = null;
    /** @type {Phaser.Tweens.Tween|null} Pulsing glow tween. */
    this._glowTween = null;
    /** @type {boolean} Whether the glow is currently active. */
    this._glowing = false;
    /** @type {number} Current glow alpha (tweened). */
    this._glowAlpha = 0;

    /** @type {Phaser.GameObjects.Graphics|null} Poison glow overlay graphics. */
    this._poisonGlowGraphics = null;
    /** @type {Phaser.Tweens.Tween|null} Poison pulsing glow tween. */
    this._poisonGlowTween = null;
    /** @type {boolean} Whether the poison glow is currently active. */
    this._poisonGlowing = false;
    /** @type {number} Current poison glow alpha (tweened). */
    this._poisonGlowAlpha = 0;

    /** @type {Phaser.GameObjects.Graphics|null} Wounded glow overlay graphics. */
    this._woundedGlowGraphics = null;
    /** @type {Phaser.Tweens.Tween|null} Wounded pulsing glow tween. */
    this._woundedGlowTween = null;
    /** @type {boolean} Whether the wounded glow is currently active. */
    this._woundedGlowing = false;
    /** @type {number} Current wounded glow alpha (tweened). */
    this._woundedGlowAlpha = 0;

    this.draw(1);
  }

  /**
   * Redraw the bar at a given ratio.
   *
   * @param {number} ratio - Health fraction in [0, 1].
   */
  draw(ratio) {
    const g = this.graphics;
    g.clear();

    // Background (dark)
    g.fillStyle(COLOR_DARK_GRAY, 0.6);
    g.fillRect(this.x, this.y, this.width, this.height);

    // Fill (white, scaled by ratio)
    const fillWidth = Math.max(0, Math.floor(this.width * ratio));
    if (fillWidth > 0) {
      g.fillStyle(COLOR_WHITE, 1);
      g.fillRect(this.x, this.y, fillWidth, this.height);
    }

    // Border
    g.lineStyle(1, COLOR_WHITE, 1);
    g.strokeRect(this.x, this.y, this.width, this.height);

    // Redraw glow overlay if active (must be redrawn on top after each bar redraw)
    if (this._glowing) {
      this._drawGlow();
    }

    // Redraw poison glow overlay if active
    if (this._poisonGlowing) {
      this._drawPoisonGlow();
    }

    // Redraw wounded glow overlay if active
    if (this._woundedGlowing) {
      this._drawWoundedGlow();
    }
  }

  /**
   * Start a subtle pulsing glow effect on the health bar to indicate
   * that the player is resting and recovering health. Uses a separate
   * Graphics object drawn on top of the bar, with a tweened alpha value.
   */
  startGlow() {
    // Don't double-start
    if (this._glowing) return;
    this._glowing = true;

    if (!this._glowGraphics) {
      this._glowGraphics = this.scene.add.graphics();
      // Ensure glow renders above the health bar graphics
      this._glowGraphics.setDepth(this.graphics.depth + 1);
    }

    this._glowAlpha = 0.15;
    this._drawGlow();

    // Use a dummy object to tween the alpha value, then redraw each update
    this._glowTarget = { alpha: 0.15 };
    this._glowTween = this.scene.tweens.add({
      targets: this._glowTarget,
      alpha: { from: 0.15, to: 0.5 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._glowAlpha = this._glowTarget.alpha;
        this._drawGlow();
      },
    });
  }

  /**
   * Redraw the glow overlay rectangle at the current glow alpha.
   * @private
   */
  _drawGlow() {
    if (!this._glowGraphics) return;
    this._glowGraphics.clear();
    this._glowGraphics.fillStyle(COLOR_WHITE, this._glowAlpha);
    this._glowGraphics.fillRoundedRect(
      this.x - 3,
      this.y - 3,
      this.width + 6,
      this.height + 6,
      3,
    );
  }

  /**
   * Stop the pulsing glow effect and clean up.
   */
  stopGlow() {
    this._glowing = false;
    this._glowAlpha = 0;

    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween.destroy();
      this._glowTween = null;
    }

    this._glowTarget = null;

    if (this._glowGraphics) {
      this._glowGraphics.clear();
      this._glowGraphics.destroy();
      this._glowGraphics = null;
    }
  }

  /**
   * Start a green pulsing glow effect on the health bar to indicate
   * the player is poisoned. Similar to the rest glow, but green-tinted.
   * Poison glow and rest glow can coexist — poison takes visual priority
   * by rendering on top.
   */
  startPoisonGlow() {
    // Don't double-start
    if (this._poisonGlowing) return;
    this._poisonGlowing = true;

    if (!this._poisonGlowGraphics) {
      this._poisonGlowGraphics = this.scene.add.graphics();
      // Ensure poison glow renders above both the health bar and rest glow
      this._poisonGlowGraphics.setDepth(this.graphics.depth + 2);
    }

    this._poisonGlowAlpha = 0.2;
    this._drawPoisonGlow();

    // Use a dummy object to tween the alpha value, then redraw each update
    this._poisonGlowTarget = { alpha: 0.2 };
    this._poisonGlowTween = this.scene.tweens.add({
      targets: this._poisonGlowTarget,
      alpha: { from: 0.2, to: 0.6 },
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._poisonGlowAlpha = this._poisonGlowTarget.alpha;
        this._drawPoisonGlow();
      },
    });
  }

  /**
   * Redraw the poison glow overlay rectangle at the current poison glow alpha.
   * Uses green color (0x33ff33) for the toxic effect.
   * @private
   */
  _drawPoisonGlow() {
    if (!this._poisonGlowGraphics) return;
    this._poisonGlowGraphics.clear();
    this._poisonGlowGraphics.fillStyle(0x33ff33, this._poisonGlowAlpha);
    this._poisonGlowGraphics.fillRoundedRect(
      this.x - 3,
      this.y - 3,
      this.width + 6,
      this.height + 6,
      3,
    );
  }

  /**
   * Stop the poison glow effect and clean up.
   */
  stopPoisonGlow() {
    this._poisonGlowing = false;
    this._poisonGlowAlpha = 0;

    if (this._poisonGlowTween) {
      this._poisonGlowTween.stop();
      this._poisonGlowTween.destroy();
      this._poisonGlowTween = null;
    }

    this._poisonGlowTarget = null;

    if (this._poisonGlowGraphics) {
      this._poisonGlowGraphics.clear();
      this._poisonGlowGraphics.destroy();
      this._poisonGlowGraphics = null;
    }
  }

  /**
   * Start a blood-red pulsing glow effect on the health bar to indicate
   * the player is wounded by spikes. Similar to the poison glow, but
   * blood-red tinted. Wounded glow renders above the poison glow.
   */
  startWoundedGlow() {
    // Don't double-start
    if (this._woundedGlowing) return;
    this._woundedGlowing = true;

    if (!this._woundedGlowGraphics) {
      this._woundedGlowGraphics = this.scene.add.graphics();
      // Ensure wounded glow renders above the health bar, rest glow, and poison glow
      this._woundedGlowGraphics.setDepth(this.graphics.depth + 3);
    }

    this._woundedGlowAlpha = 0.2;
    this._drawWoundedGlow();

    // Use a dummy object to tween the alpha value, then redraw each update
    this._woundedGlowTarget = { alpha: 0.2 };
    this._woundedGlowTween = this.scene.tweens.add({
      targets: this._woundedGlowTarget,
      alpha: { from: 0.2, to: 0.6 },
      duration: 450,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._woundedGlowAlpha = this._woundedGlowTarget.alpha;
        this._drawWoundedGlow();
      },
    });
  }

  /**
   * Redraw the wounded glow overlay rectangle at the current wounded glow alpha.
   * Uses blood-red color (0xcc2222) for the wounded effect.
   * @private
   */
  _drawWoundedGlow() {
    if (!this._woundedGlowGraphics) return;
    this._woundedGlowGraphics.clear();
    this._woundedGlowGraphics.fillStyle(0xcc2222, this._woundedGlowAlpha);
    this._woundedGlowGraphics.fillRoundedRect(
      this.x - 3,
      this.y - 3,
      this.width + 6,
      this.height + 6,
      3,
    );
  }

  /**
   * Stop the wounded glow effect and clean up.
   */
  stopWoundedGlow() {
    this._woundedGlowing = false;
    this._woundedGlowAlpha = 0;

    if (this._woundedGlowTween) {
      this._woundedGlowTween.stop();
      this._woundedGlowTween.destroy();
      this._woundedGlowTween = null;
    }

    this._woundedGlowTarget = null;

    if (this._woundedGlowGraphics) {
      this._woundedGlowGraphics.clear();
      this._woundedGlowGraphics.destroy();
      this._woundedGlowGraphics = null;
    }
  }

  /** Clean up graphics object. */
  destroy() {
    this.stopGlow();
    this.stopPoisonGlow();
    this.stopWoundedGlow();

    if (this.graphics) {
      this.graphics.destroy();
      this.graphics = null;
    }
  }
}
